import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="treegrid--row"
export default class TreegridRowController extends Controller {
	static targets = ["trigger"]

	connect() {
		const firstElement = this.firstElement
		this.element.setAttribute("tabindex", this.element == firstElement ? "0" : "-1")

		this.element.addEventListener("keydown", this.onKeyDown)
		this.element.addEventListener("dblclick", this.onDoubleClick)
	}

	disconnect() {
		this.element.removeEventListener("keydown", this.onKeyDown)
		this.element.removeEventListener("dblclick", this.onDoubleClick)
	}

	/** @param {HTMLElement} target */
	triggerTargetConnected(target) {
		target.addEventListener("click", this.onTriggerClick)
		target.addEventListener("dblclick", this.onTriggerDoubleClick)
	}

	/** @param {HTMLElement} target */
	triggerTargetDisconnected(target) {
		target.removeEventListener("click", this.onTriggerClick)
		target.removeEventListener("dblclick", this.onTriggerDoubleClick)
	}

	/** @param {KeyboardEvent} event */
	onKeyDown = (event) => {
		if (!this.isFocused) return

		switch (event.key) {
			case "ArrowRight": {
				if (this.isExpandable && this.isCollapsed) {
					this.expand()
					break
				}

				if (this.firstCell) {
					this.firstCell.focus()
					break
				}
				return
			}
			case "ArrowLeft": {
				if (this.isExpandable && this.isExpanded) {
					this.collapse()
					break
				}

				if (this.parent) {
					this.parent.focus()
					break
				}
				return
			}
			case "ArrowDown": {
				if (this.next) {
					this.next.focus()
					break
				}
				return
			}
			case "ArrowUp": {
				if (this.prev) {
					this.prev.focus()
					break
				}
				return
			}
			case "Home": {
				if (this.first) {
					this.first.focus()
					break
				}
				return
			}
			case "End": {
				if (this.last) {
					this.last.focus()
					break
				}
				return
			}
			default: return
		}

		event.preventDefault()
		event.stopPropagation()
	}

	/** @param {MouseEvent} event */
	onDoubleClick = (event) => {
		const element = event.target.matches(`[data-controller="${this.identifier}"]`)
			? event.target
			: event.target.closest(`[data-controller="${this.identifier}"]`)
		const row = this.#controllerForElement(element)

		row.isExpandable && row.toggle()
	}

	/** @param {PointerEvent} event */
	onTriggerClick = (event) => { event.stopPropagation(); this.toggle(); }

	/** @param {MouseEvent} event */
	onTriggerDoubleClick = (event) => event.stopPropagation()

	get index() {
		return this.treegrid.rows.indexOf(this)
	}

	get level() {
		return parseInt(this.element.getAttribute("aria-level"))
	}

	get isFocused() {
		return this.element == document.activeElement
	}

	get isExpandable() {
		return this.element.hasAttribute("aria-expanded")
	}

	get isExpanded() {
		return this.element.getAttribute("aria-expanded") == "true"
	}

	get isCollapsed() {
		return this.element.getAttribute("aria-expanded") == "false"
	}

	/** @returns {import("../treegrid_controller").default} */
	get treegrid() {
		return this.application.getControllerForElementAndIdentifier(
			this.element.closest(`[data-controller="treegrid"]`), "treegrid"
		)
	}

	/** @returns {TreegridRowController} */
	get parent() {
		for (let sibling = this.element; sibling = sibling.previousElementSibling;) {
			const row = this.#controllerForElement(sibling)

			if (row.level == this.level - 1) {
				return row
			}
		}
	}

	get children() {
		/** @type {TreegridRowController[]} */
		const children = []

		for (let sibling = this.element; sibling = sibling.nextElementSibling;) {
			const row = this.#controllerForElement(sibling)

			if (row.level == this.level + 1) {
				children.push(row)
			}
		}

		return children
	}

	get descendants() {
		/** @type {TreegridRowController[]} */
		const descendants = []

		for (let sibling = this.element; sibling = sibling.nextElementSibling;) {
			const row = this.#controllerForElement(sibling)

			if (row.level > this.level) {
				descendants.push(row)
			} else break
		}

		return descendants
	}

	get firstElement() {
		return this.treegrid.element.querySelector(
			`[data-controller="${this.identifier}"]:not([hidden])`
		)
	}

	get first() {
		return this.treegrid.firstRow
	}

	get last() {
		return this.treegrid.lastRow
	}

	get next() {
		return this.treegrid.rows[this.index + 1]
	}

	get prev() {
		return this.treegrid.rows[this.index - 1]
	}

	/** @type {import("./cell_controller").default[]} */
	get cells() {
		return Array.from(this.element.querySelectorAll(
			`[data-controller="treegrid--cell"]:not([hidden])`
		)).map(element => this.application.getControllerForElementAndIdentifier(
			element, "treegrid--cell"
		))
	}

	get firstCell() {
		return this.cells[0]
	}

	get lastCell() {
		const cells = this.cells

		return cells[cells.length - 1]
	}

	expand() {
		this.element.setAttribute("aria-expanded", "true")

		for (const child of this.children) {
			child.element.removeAttribute("hidden")

			if (child.isExpandable && child.isExpanded) {
				child.expand()
			}
		}
	}

	collapse() {
		this.element.setAttribute("aria-expanded", "false")

		this.descendants.forEach(descendant => {
			descendant.element.setAttribute("hidden", "")

			if (descendant.element.getAttribute("tabindex") == "0") {
				descendant.element.setAttribute("tabindex", "-1")
				this.firstElement.setAttribute("tabindex", "0")
			}
		})
	}

	toggle() {
		this.isExpanded ? this.collapse() : this.expand()
	}

	focus() {
		if (document.activeElement?.matches(`[data-controller="${this.identifier}"]`)) {
			document.activeElement.setAttribute("tabindex", "-1")
		}

		this.element.setAttribute("tabindex", "0")
		this.element.focus()
	}

	/** @returns {TreegridRowController} */
	#controllerForElement = (element) => {
		return this.application.getControllerForElementAndIdentifier(
			element, this.identifier
		)
	}
}

